import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "439",
  "slug": "what-is-plate-tectonics",
  "title": "What Is Plate Tectonics?",
  "excerpt": "Earthquakes, volcanoes, mountain ranges, and the very shape of the continents all come from the same slow-motion process. Here is plate tectonics explained in plain language.",
  "date": "2026-06-14",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1704739410998-564ae85cc537?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx2b2xjYW5pYyUyMG1vdW50YWluJTIwbGFuZHNjYXBlfGVufDF8MHx8fDE3ODE0MjUwODB8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Plate tectonics is the theory that Earth's rigid outer shell is broken into large plates that slowly move over the hotter, softer layer beneath. Their grinding, colliding, and pulling apart builds mountains, opens oceans, and drives earthquakes and volcanoes.",
  "content": [
    {
      "type": "paragraph",
      "text": "The continents look permanent, fixed in place since the beginning of time. They are not. They are rafts of rock drifting across the surface of the planet at roughly the speed your fingernails grow, and over millions of years that slow drift has opened oceans, raised mountain ranges, and rearranged the entire map of the world. The framework that explains it all is plate tectonics, one of the great unifying ideas in all of science."
    },
    {
      "type": "heading",
      "text": "The basic picture"
    },
    {
      "type": "paragraph",
      "text": "Earth's outer shell, called the lithosphere, is not a single solid layer. It is broken into about a dozen large plates and several smaller ones, like a cracked eggshell. These plates float on the hotter, slowly flowing rock of the mantle beneath them. Heat rising from deep inside the planet keeps the mantle churning, and that churning drags the plates along with it, a few centimeters a year."
    },
    {
      "type": "paragraph",
      "text": "Because the plates are all moving, the action happens at their edges, where they meet. Geologists recognize three main kinds of boundary, and almost every dramatic landscape on Earth traces back to one of them."
    },
    {
      "type": "heading",
      "text": "The three types of plate boundary"
    },
    {
      "type": "list",
      "items": [
        "Divergent boundaries, where plates pull apart. Magma rises to fill the gap and creates new crust. This is how mid-ocean ridges form on the seafloor, and how the East African Rift is slowly tearing a continent in two.",
        "Convergent boundaries, where plates collide. One plate may dive beneath another in a process called subduction, building deep ocean trenches and chains of volcanoes, or two continents may crumple together to raise mountains like the Himalayas.",
        "Transform boundaries, where plates slide past each other horizontally. The grinding builds up stress that releases as earthquakes, as along California's San Andreas Fault."
      ]
    },
    {
      "type": "heading",
      "text": "The idea behind the Ring of Fire"
    },
    {
      "type": "paragraph",
      "text": "Plate tectonics explains why earthquakes and volcanoes are not scattered randomly but line up along clear belts. The famous Ring of Fire around the edges of the Pacific Ocean is simply a string of convergent and transform boundaries, where plates collide and grind. Map the world's earthquakes and volcanoes and you are essentially drawing the outlines of the plates themselves."
    },
    {
      "type": "heading",
      "text": "The major plates"
    },
    {
      "type": "paragraph",
      "text": "Geologists usually count seven or eight major plates, along with a long list of smaller ones. The big players include the Pacific Plate, the largest of all, plus the North American, South American, Eurasian, African, Antarctic, and Indo-Australian plates. Their edges crisscross the planet, and where they meet, the ground is rarely quiet. Most of the world's earthquakes, volcanoes, and mountain ranges sit along these boundaries rather than in the calm interiors of the plates."
    },
    {
      "type": "heading",
      "text": "How we know they move"
    },
    {
      "type": "paragraph",
      "text": "The motion is far too slow to feel, but we can measure it with remarkable precision. Networks of GPS stations track how far apart two points drift each year, down to the millimeter. Even more compelling is the evidence frozen into the ocean floor: as new crust forms at mid-ocean ridges, it records the direction of Earth's magnetic field, which flips every so often. The result is a series of magnetic stripes, mirrored on either side of the ridge, that act like a tape recording of seafloor spreading and confirm the plates have been on the move for a very long time."
    },
    {
      "type": "heading",
      "text": "From Pangaea to today"
    },
    {
      "type": "paragraph",
      "text": "The first version of this idea came from Alfred Wegener, who in the early twentieth century noticed that the coastlines of South America and Africa fit together like puzzle pieces, and that matching fossils and rock formations appeared on both. He proposed that the continents had once been joined in a single supercontinent, later called Pangaea, before drifting apart. His idea was mocked at first, but decades later, evidence from the ocean floor confirmed it and gave us modern plate tectonics."
    },
    {
      "type": "heading",
      "text": "Reading the results from above"
    },
    {
      "type": "paragraph",
      "text": "Compare a physical map with a plate-boundary map and trace where mountain ranges, ocean trenches, and volcanoes occur. Note the features that fall away from present-day boundaries as well as those that follow them."
    }
  ]
};
